import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageSection from "./PageSection";
import ArrowIcon from "./ArrowIcon";

interface ProductPageBodyProps {
  /** Product key under kode.products in the translation file. */
  productKey: "bogowi" | "bweb3" | "easychain" | "bdid" | "islasVivas" | "productivity" | "documents";
  /** Block (Wallets or AI & Data) breadcrumb context — controls i18n labels. */
}

interface Scenario { who: string; h: string; p: string }
type Commercial = [string, string];

/**
 * Body of a Level-2 product page. The breadcrumb and the AppNav are
 * rendered by the page wrapper that uses this component, since they
 * differ per block (wallets vs ai-data).
 */
const ProductPageBody: React.FC<ProductPageBodyProps> = ({ productKey }) => {
  const { t } = useTranslation();
  const k = (suffix: string) => `kode.products.${productKey}.${suffix}`;

  const scenarios = t(k("scenarios"), { returnObjects: true }) as unknown as Scenario[];
  const commercial = t(k("commercial"), { returnObjects: true }) as unknown as Commercial[];
  const liveUrl = t(k("liveUrl"));
  const liveIsExternal = liveUrl.startsWith("http");

  return (
    <>
      {/* 01 / Hero */}
      <PageSection marker={t(k("sectionLabel"))} noTop topPadClass="!pt-12">
        <h1 className="section-h-lg">{t(k("h1"))}</h1>
        <p className="text-body-lg text-ink-70 max-w-prose mt-6" style={{ fontSize: "1.25rem", lineHeight: 1.55 }}>
          {t(k("lead"))}
        </p>
      </PageSection>

      {/* 02 / Situation */}
      <PageSection marker={t("kode.common.situationMarker")}>
        <h2 className="section-h">{t(k("situationH2"))}</h2>
        <p className="section-sub">{t(k("situationSub"))}</p>
      </PageSection>

      {/* 03 / What changes */}
      <PageSection marker={t("kode.common.changesMarker")}>
        <h2 className="section-h">{t(k("changesH2"))}</h2>
        <div className="outcomes">
          {[1, 2, 3, 4, 5].map((n) => (
            <div className="row" key={n}>
              <div className="ix">{String(n).padStart(2, "0")}</div>
              <div>
                <h4>{t(k(`outcome${n}H`))}</h4>
                <p>{t(k(`outcome${n}P`))}</p>
              </div>
            </div>
          ))}
        </div>
      </PageSection>

      {/* 04 / In practice */}
      <PageSection marker={t("kode.common.inPracticeMarker")}>
        <h2 className="section-h">{t("kode.common.scenariosTitle")}</h2>
        <div className="scenarios">
          {scenarios.map((s, i) => (
            <div className="scn" key={i}>
              <span className="who">{s.who}</span>
              <h4>{s.h}</h4>
              <p>{s.p}</p>
            </div>
          ))}
        </div>
      </PageSection>

      {/* 05 / Commercial */}
      <PageSection marker={t("kode.common.commercialMarker")}>
        <h2 className="section-h">{t("kode.common.commercialTitle")}</h2>
        <div className="lines">
          {commercial.map(([key, val], i) => (
            <div className="row" key={i}>
              <div className="k">{key}</div>
              <div className="v">{val}</div>
            </div>
          ))}
        </div>
      </PageSection>

      {/* 06 / Live today */}
      <PageSection marker={t("kode.common.liveMarker")}>
        <div className="prose">
          <h2 className="section-h">{t(k("liveH2"))}</h2>
          <p>{t(k("liveBody"))}</p>
          {liveIsExternal ? (
            <a href={liveUrl} target="_blank" rel="noreferrer" className="more">
              {t(k("liveLink"))}
              <ArrowIcon />
            </a>
          ) : (
            <Link to={liveUrl} className="more">
              {t(k("liveLink"))}
              <ArrowIcon />
            </Link>
          )}
        </div>
      </PageSection>

      {/* 07 / Talk */}
      <PageSection marker={t("kode.common.talkMarker")}>
        <h3 className="section-h">{t(k("talkH3"))}</h3>
        <p className="section-sub">{t(k("talkSub"))}</p>
        <div className="flex flex-wrap gap-3 mt-5">
          <Link to="/contact" className="btn btn-primary">
            {t("kode.common.bookCall")}
            <ArrowIcon className="h-4 w-4" />
          </Link>
          <a href="mailto:hugo@kode.zone" className="btn btn-secondary">
            hugo@kode.zone
          </a>
        </div>
      </PageSection>
    </>
  );
};

export default ProductPageBody;
